/**
 * The formatting of a parameter (ADR-119 decision 2) with `Intl`, which is ECMAScript and so open to a pure package:
 * a number with `Intl.NumberFormat`, a duration with its `unit` style, a list with `Intl.ListFormat`, a moment with
 * `Intl.DateTimeFormat`, and the plural category with `Intl.PluralRules`. The locale and any time zone are always
 * named by the caller; nothing here reads the runtime's own. `Intl.DurationFormat` is not used: it is above the
 * page's browser floor.
 */

import type { DateParam, DurationParam, KeyParam, ListParam } from "./types.ts";
import type { PluralCategory } from "./values.ts";

/**
 * The most formatters one cache holds. A cache is cleared when it is full, which only costs a rebuild.
 */
const MAX_FORMATTERS = 256;

/**
 * Formatters already built, one cache per kind, by locale and options; null for one `Intl` refused to build.
 */
const PLURALS = new Map<string, Intl.PluralRules | null>();

/**
 * Number and duration formatters already built.
 */
const NUMBERS = new Map<string, Intl.NumberFormat | null>();

/**
 * List formatters already built.
 */
const LISTS = new Map<string, Intl.ListFormat | null>();

/**
 * Date and time formatters already built.
 */
const DATES = new Map<string, Intl.DateTimeFormat | null>();

/**
 * Builds a formatter once per locale and options, and holds it. A malformed locale or time zone makes `Intl` throw
 * a `RangeError`, which is held as null so a value is never refused twice and `say` never throws.
 *
 * @param cache The kind's cache.
 * @param id The cache's key: the locale and the options as a JSON array, so no two ever read alike.
 * @param build Builds the formatter.
 * @returns The formatter, or null when `Intl` refuses the locale or the options.
 */
function held<F>(cache: Map<string, F | null>, id: string, build: () => F): F | null {
  const known = cache.get(id);
  if (known !== undefined) {
    return known;
  }
  let made: F | null;
  try {
    made = build();
  } catch {
    made = null;
  }
  if (cache.size >= MAX_FORMATTERS) {
    cache.clear();
  }
  cache.set(id, made);
  return made;
}

/**
 * The plural category of a count in a locale, `other` when the count is not a finite number or the locale is
 * refused.
 *
 * @param locale The BCP 47 locale.
 * @param count The count.
 * @returns The category whose form is shown.
 */
export function pluralCategory(locale: string, count: unknown): PluralCategory {
  if (typeof count !== "number" || !Number.isFinite(count)) {
    return "other";
  }
  const rules = held(PLURALS, locale, () => new Intl.PluralRules(locale));
  return rules === null ? "other" : rules.select(count);
}

/**
 * Formats a number in a locale.
 *
 * @param locale The BCP 47 locale.
 * @param value The number.
 * @returns The formatted number, or the empty string when the locale is refused.
 */
export function formatNumber(locale: string, value: number): string {
  return held(NUMBERS, JSON.stringify([locale]), () => new Intl.NumberFormat(locale))?.format(value) ?? "";
}

/**
 * Formats a duration in its unit.
 *
 * @param locale The BCP 47 locale.
 * @param value The duration.
 * @returns The formatted duration, or the empty string when the locale is refused.
 */
export function formatDuration(locale: string, value: DurationParam): string {
  const width = value.width ?? "long";
  return held(NUMBERS, JSON.stringify([locale, value.unit, width]), () => new Intl.NumberFormat(locale, { style: "unit", unit: value.unit, unitDisplay: width }))?.format(value.duration) ?? "";
}

/**
 * Joins a list in a locale.
 *
 * @param locale The BCP 47 locale.
 * @param value The list.
 * @returns The joined list, or the empty string when the locale is refused.
 */
export function formatList(locale: string, value: ListParam): string {
  const type = value.join === "or" ? "disjunction" : "conjunction";
  return held(LISTS, JSON.stringify([locale, type]), () => new Intl.ListFormat(locale, { type, style: "long" }))?.format(value.list) ?? "";
}

/**
 * Formats a moment in a locale and the time zone it names: the medium date, the short time, or both.
 *
 * @param locale The BCP 47 locale.
 * @param value The moment.
 * @returns The formatted moment, or the empty string when the locale, the time zone or the moment is refused (a moment
 * past `Intl`'s range of about 275,000 years either side of the epoch is).
 */
export function formatDate(locale: string, value: DateParam): string {
  if (!Number.isFinite(value.date)) {
    return "";
  }
  const options: Intl.DateTimeFormatOptions =
    value.show === "date" ? { dateStyle: "medium", timeZone: value.timeZone } : value.show === "time" ? { timeStyle: "short", timeZone: value.timeZone } : { dateStyle: "medium", timeStyle: "short", timeZone: value.timeZone };
  const format = held(DATES, JSON.stringify([locale, value.show, value.timeZone]), () => new Intl.DateTimeFormat(locale, options));
  if (format === null) {
    return "";
  }
  try {
    return format.format(value.date);
  } catch {
    return "";
  }
}

/**
 * Whether a value is a plain object holding a member of the given name.
 *
 * @param value Anything.
 * @param member The member's name.
 * @returns True when the member is the object's own.
 */
function holds(value: unknown, member: string): value is { readonly [key: string]: unknown } {
  return typeof value === "object" && value !== null && !Array.isArray(value) && Object.hasOwn(value, member);
}

/**
 * Whether a parameter is a nested key.
 *
 * @param value Anything.
 * @returns True for `{ k: string }`.
 */
export function isKeyParam(value: unknown): value is KeyParam {
  return holds(value, "k") && typeof value["k"] === "string";
}

/**
 * Whether a parameter is a list.
 *
 * @param value Anything.
 * @returns True for `{ list: string[], join?: "and" | "or" }`.
 */
function isListParam(value: unknown): value is ListParam {
  return holds(value, "list") && Array.isArray(value["list"]) && value["list"].every((item) => typeof item === "string") && (value["join"] === undefined || value["join"] === "and" || value["join"] === "or");
}

/**
 * Whether a parameter is a moment.
 *
 * @param value Anything.
 * @returns True for `{ date: number, timeZone: string, show }`.
 */
function isDateParam(value: unknown): value is DateParam {
  return holds(value, "date") && typeof value["date"] === "number" && typeof value["timeZone"] === "string" && (value["show"] === "date" || value["show"] === "time" || value["show"] === "dateTime");
}

/**
 * Whether a parameter is a duration.
 *
 * @param value Anything.
 * @returns True for `{ duration: number, unit, width? }`.
 */
function isDurationParam(value: unknown): value is DurationParam {
  return (
    holds(value, "duration") &&
    typeof value["duration"] === "number" &&
    (value["unit"] === "minute" || value["unit"] === "hour" || value["unit"] === "day") &&
    (value["width"] === undefined || value["width"] === "long" || value["width"] === "short")
  );
}

/**
 * Formats a parameter that is not a nested key: a string as itself, a number, a list, a moment or a duration. A
 * parameter of any other shape, which only an untyped caller can pass, is shown as nothing.
 *
 * @param locale The BCP 47 locale.
 * @param value The parameter.
 * @returns Its text.
 */
export function formatParam(locale: string, value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return formatNumber(locale, value);
  }
  if (isListParam(value)) {
    return formatList(locale, value);
  }
  if (isDateParam(value)) {
    return formatDate(locale, value);
  }
  if (isDurationParam(value)) {
    return formatDuration(locale, value);
  }
  return "";
}
